// Smart CTAs that provide contextual hooks to the Experience Centre
import React from 'react';
import { Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  PlayArrow, 
  Calculate, 
  Engineering, 
  Assessment 
} from '@mui/icons-material';
import marketingTheme from '../themes/marketingTheme';
import { isFeatureEnabled } from '../utils/FeatureFlags';

// Subtle contextual CTA that suggests experience centre features
export const SmartCTA = ({ 
  context, 
  fallbackHref = '/contact',
  fallbackText = 'Learn More',
  size = 'large',
  variant = 'contained',
  sx = {},
  ...props 
}) => {
  const navigate = useNavigate();

  // Only show smart CTAs if SaaS onboarding is enabled
  if (!isFeatureEnabled('SAAS_ONBOARDING')) {
    return (
      <Button 
        variant={variant}
        size={size}
        href={fallbackHref}
        sx={sx}
        {...props}
      >
        {fallbackText}
      </Button>
    );
  }

  const getContextualAction = () => {
    switch (context) {
      case 'safety-analysis':
        return {
          text: 'Analyse your workplace safety',
          path: '/demo?context=safety&positioning=value_teaser?context=marketing',
          icon: <Assessment sx={{ mr: 1 }} />
        };
      
      case 'productivity-analysis':
        return {
          text: 'See productivity insights',
          path: '/demo?context=productivity&positioning=value_teaser?context=marketing',
          icon: <PlayArrow sx={{ mr: 1 }} />
        };
      
      case 'roi-calculation':
        return {
          text: 'Calculate your ROI',
          path: '/business-case?focus=roi&context=marketing',
          icon: <Calculate sx={{ mr: 1 }} />
        };
      
      case 'implementation-planning':
        return {
          text: 'Plan your deployment',
          path: '/implementation?phase=planning&context=marketing',
          icon: <Engineering sx={{ mr: 1 }} />
        };
      
      case 'experience-demo':
        return {
          text: 'Upload Workplace Video',
          path: '/demo',
          icon: <PlayArrow sx={{ mr: 1 }} />
        };
      
      default:
        return {
          text: fallbackText,
          path: fallbackHref,
          icon: null
        };
    }
  };

  const action = getContextualAction();

  const handleClick = () => {
    // Track the contextual engagement
    if (window.gtag) {
      window.gtag('event', 'smart_cta_click', {
        'custom_parameter': context,
        'send_to': 'AW-16872295585/O8D7CKns6Z4aEKGZq-0-'
      });
    }
    
    navigate(action.path);
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      startIcon={action.icon}
      sx={{
        ...sx,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          ...sx['&:hover']
        }
      }}
      {...props}
    >
      {action.text}
    </Button>
  );
};

// Subtle chip that hints at experience centre capabilities
export const SmartChip = ({ 
  context, 
  label, 
  onClick,
  sx = {},
  ...props 
}) => {
  const navigate = useNavigate();

  if (!isFeatureEnabled('SAAS_ONBOARDING')) {
    return null; // Don't show if feature disabled
  }

  const getContextualPath = () => {
    switch (context) {
      case 'try-analysis':
        return '/demo?context=chip-engagement';
      case 'calculate-savings':
        return '/business-case?focus=roi&context=chip-engagement';
      case 'plan-implementation':
        return '/implementation?context=chip-engagement';
      default:
        return '/get-started';
    }
  };

  const handleChipClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(getContextualPath());
    }
  };

  return (
    <Chip
      label={label}
      onClick={handleChipClick}
      clickable
      sx={{
        backgroundColor: 'rgba(57, 102, 237, 0.1)',
        color: marketingTheme.palette.primary.main,
        border: `1px solid ${marketingTheme.palette.primary.main}4D`,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(57, 102, 237, 0.2)',
          transform: 'translateY(-1px)',
        },
        ...sx
      }}
      {...props}
    />
  );
};

export default SmartCTA;